import { createContext, useContext, useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { signOut, getCurrentUser } from 'aws-amplify/auth';
import { apiClient, ApiError } from '../utils/api-client';

interface UserAccount {
  cognitoUserId: string;
  email: string;
  stripeCustomerId?: string;
  displayCurrency?: 'USD' | 'GBP' | 'EUR';
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: UserAccount | null;
  isSessionValid: boolean;
  loading: boolean;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  refreshUser: () => Promise<void>;
  fetchUserAccount: () => Promise<UserAccount | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserAccount | null>(null);
  const [isSessionValid, setIsSessionValid] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchUserAccount = async (): Promise<UserAccount | null> => {
    try {
      const response = await apiClient.get('/accounts/user/account');
      const userData = response.account;
      setUser(userData);
      return userData;
    } catch (error) {
      // 404 = genuinely no account (new user, needs onboarding)
      if (error instanceof ApiError && error.status === 404) {
        setUser(null);
        return null;
      }
      // Any other error is transient — propagate so callers can show retry UI
      console.error('Failed to fetch user account:', error);
      setUser(null);
      throw error;
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        await getCurrentUser();
        setIsSessionValid(true);

        try {
          await fetchUserAccount();
        } catch (accountErr) {
          // Expected for new users
        }
      } catch (err) {
        setIsSessionValid(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  const refreshSession = async () => {
    try {
      await getCurrentUser();
      setIsSessionValid(true);
      try {
        await fetchUserAccount();
      } catch {
        // Expected for new users
      }
    } catch {
      setIsSessionValid(false);
      setUser(null);
    }
  };

  const logout = async () => {
    try {
      await signOut({ global: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('polyreader_vocab');
      // Clear all per-article selection history
      const selectionKeys = Object.keys(localStorage).filter(k => k.startsWith('polyreader_selections_'));
      for (const key of selectionKeys) localStorage.removeItem(key);
      document.documentElement.classList.remove('dark');
      setUser(null);
      setIsSessionValid(false);
    }
  };

  const refreshUser = async () => {
    if (isSessionValid) {
      await fetchUserAccount();
    }
  };

  const value = {
    user,
    isSessionValid,
    loading,
    logout,
    refreshSession,
    refreshUser,
    fetchUserAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
